// src/services/supportChatService.js

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  limitToLast,
  startAfter,
  endBefore,
  getDocs,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { firestore } from '../firebase/firebase';
import Axios from '../actions/Axios';
import { SEND_PUSH_NOTIFICATION } from '../api/notifications';
import { API_UPLOAD_SUPPORT_MEDIA } from '../ApiUrl';

export const SUPPORT_CHATS_COLLECTION = 'support_chats';
export const ADMIN_ID = '60796b3cccefb0dc1fac984c'; // Canonical Config.ADMIN_ID required by mobile app

export const ALLOWED_EXTENSIONS = {
  image: ['png', 'jpg', 'jpeg'],
  pdf: ['pdf'],
  doc: ['doc', 'docx'],
};

/**
 * Validates the file extension against the contract and returns the messageType string.
 * @param {string} fileName
 * @returns {'image'|'pdf'|'doc'|null}
 */
export const getMessageTypeFromFileName = (fileName) => {
  if (!fileName || typeof fileName !== 'string') return null;
  const ext = fileName.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
  if (ALLOWED_EXTENSIONS.image.includes(ext)) return 'image';
  if (ALLOWED_EXTENSIONS.pdf.includes(ext)) return 'pdf';
  if (ALLOWED_EXTENSIONS.doc.includes(ext)) return 'doc';
  return null;
};

/**
 * Normalizes conversationStatus to ensure reliable state handling
 */
export const normalizeConversationStatus = (status) => {
  if (!status) return 'open';
  const s = String(status).toLowerCase().trim();
  return s === 'ended' || s === 'closed' ? 'ended' : 'open';
};

/**
 * Subscribes to the threads list with real-time updates.
 *
 * @param {Object} options
 * @param {number} options.pageSize Number of threads to fetch per page
 * @param {Function} options.onUpdate Callback with ({ threads, lastDoc, hasMore })
 * @param {Function} options.onError Error callback
 * @returns {Function} Unsubscribe function
 */
export const subscribeToThreads = ({ pageSize = 25, onUpdate, onError } = {}) => {
  if (!firestore) {
    if (onError)
      onError(new Error('Firestore is not initialized. Please check Firebase configuration.'));
    return () => {};
  }

  try {
    const chatsRef = collection(firestore, SUPPORT_CHATS_COLLECTION);
    const q = query(chatsRef, orderBy('updatedAt', 'desc'), limit(pageSize));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const threads = [];
        snapshot.forEach((docSnap) => {
          threads.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });

        const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
        const hasMore = snapshot.docs.length >= pageSize;

        if (onUpdate) {
          onUpdate({ threads, lastDoc, hasMore });
        }
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('[SupportChatService] subscribeToThreads error:', error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[SupportChatService] subscribeToThreads failed:', error);
    if (onError) onError(error);
    return () => {};
  }
};

/**
 * Loads additional older threads for pagination (lazy loading).
 *
 * @param {Object} lastDoc Last visible Firestore DocumentSnapshot
 * @param {number} pageSize Batch size
 * @returns {Promise<{ threads: Array, lastDoc: Object, hasMore: boolean }>}
 */
export const loadMoreThreads = async (lastDoc, pageSize = 20) => {
  if (!firestore || !lastDoc) {
    return { threads: [], lastDoc: null, hasMore: false };
  }

  try {
    const chatsRef = collection(firestore, SUPPORT_CHATS_COLLECTION);
    const q = query(chatsRef, orderBy('updatedAt', 'desc'), startAfter(lastDoc), limit(pageSize));

    const snapshot = await getDocs(q);
    const threads = [];
    snapshot.forEach((docSnap) => {
      threads.push({
        id: docSnap.id,
        ...docSnap.data(),
      });
    });

    const nextLastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
    const hasMore = snapshot.docs.length >= pageSize;

    return { threads, lastDoc: nextLastDoc, hasMore };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[SupportChatService] loadMoreThreads error:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time messages within a specific conversation thread.
 * Orders strictly by createdAt asc as required by mobile contract.
 *
 * @param {string} threadId 24-character hexadecimal conversation ID
 * @param {Object} options
 * @param {number} options.pageSize Number of recent messages to load initially
 * @param {Function} options.onUpdate Callback receiving ({ messages, oldestDoc, hasMoreOlder })
 * @param {Function} options.onError Error callback
 * @returns {Function} Unsubscribe function
 */
const parseMillis = (val) => {
  if (!val) return 0;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (typeof val.toDate === 'function') return val.toDate().getTime();
  if (val instanceof Date) return val.getTime();
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const p = Date.parse(val);
    return isNaN(p) ? 0 : p;
  }
  return 0;
};

export const subscribeToMessages = (threadId, { pageSize = 40, onUpdate, onError } = {}) => {
  if (!firestore || !threadId) {
    return () => {};
  }

  try {
    const messagesRef = collection(firestore, SUPPORT_CHATS_COLLECTION, threadId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'), limitToLast(pageSize));

    const handleSnapshot = (snapshot) => {
      const messages = [];
      snapshot.forEach((docSnap) => {
        messages.push({
          id: docSnap.id,
          _id: docSnap.id,
          ...docSnap.data(),
        });
      });

      // Sort in memory to guarantee perfect ascending order
      messages.sort(
        (a, b) => parseMillis(a.createdAt || a.timestamp) - parseMillis(b.createdAt || b.timestamp)
      );

      const oldestDoc = snapshot.docs[0] || null;
      const hasMoreOlder = snapshot.docs.length >= pageSize;

      if (onUpdate) {
        onUpdate({ messages, oldestDoc, hasMoreOlder });
      }
    };

    let unsubscribe = onSnapshot(q, handleSnapshot, (error) => {
      // eslint-disable-next-line no-console
      console.warn(
        '[SupportChatService] query with orderBy failed, falling back to simple query:',
        error
      );
      // Fallback without orderBy in case index or field type issue
      try {
        const fallbackQ = query(messagesRef, limit(pageSize));
        unsubscribe = onSnapshot(fallbackQ, handleSnapshot, (fallbackErr) => {
          // eslint-disable-next-line no-console
          console.error('[SupportChatService] fallback subscribeToMessages error:', fallbackErr);
          if (onError) onError(fallbackErr);
        });
      } catch (fbErr) {
        if (onError) onError(error);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[SupportChatService] subscribeToMessages failed:', error);
    if (onError) onError(error);
    return () => {};
  }
};

/**
 * Loads older messages before the currently oldest message (lazy loading scroll-up).
 *
 * @param {string} threadId
 * @param {Object} oldestDoc
 * @param {number} pageSize
 * @returns {Promise<{ olderMessages: Array, newOldestDoc: Object, hasMoreOlder: boolean }>}
 */
export const loadOlderMessages = async (threadId, oldestDoc, pageSize = 25) => {
  if (!firestore || !threadId || !oldestDoc) {
    return { olderMessages: [], newOldestDoc: null, hasMoreOlder: false };
  }

  try {
    const messagesRef = collection(firestore, SUPPORT_CHATS_COLLECTION, threadId, 'messages');
    const q = query(
      messagesRef,
      orderBy('createdAt', 'asc'),
      endBefore(oldestDoc),
      limitToLast(pageSize)
    );

    const snapshot = await getDocs(q);
    const olderMessages = [];
    snapshot.forEach((docSnap) => {
      olderMessages.push({
        id: docSnap.id,
        _id: docSnap.id,
        ...docSnap.data(),
      });
    });

    const newOldestDoc = snapshot.docs[0] || null;
    const hasMoreOlder = snapshot.docs.length >= pageSize;

    return { olderMessages, newOldestDoc, hasMoreOlder };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[SupportChatService] loadOlderMessages error:', error);
    throw error;
  }
};

/**
 * Sends an admin message following the exact GiftedChat and mobile read-receipt contract.
 *
 * @param {Object} params
 * @param {string} params.threadId 24-char hex conversation ID
 * @param {string} params.text Message text
 * @param {string} params.adminName Current responding admin's display name
 * @param {string} [params.adminAvatar] Optional avatar image URL
 * @param {string} [params.customerId] Target customer ID for push notification
 * @returns {Promise<Object>} The added message reference
 */
export const sendAdminMessage = async ({
  threadId,
  text,
  adminName = 'Support Admin',
  adminAvatar = '',
  customerId = null,
}) => {
  if (!firestore || !threadId || !text || !text.trim()) {
    throw new Error('Invalid arguments for sendAdminMessage');
  }

  const cleanText = text.trim();
  const threadRef = doc(firestore, SUPPORT_CHATS_COLLECTION, threadId);
  const messagesRef = collection(firestore, SUPPORT_CHATS_COLLECTION, threadId, 'messages');

  // Exact mobile read-receipt & GiftedChat contract:
  // Must have read: false on every admin message
  // Must use exact Config.ADMIN_ID
  // createdAt ONLY (no timestamp field)
  const messageData = {
    text: cleanText,
    createdAt: serverTimestamp(),
    read: false,
    senderId: ADMIN_ID,
    senderRole: 'admin',
    messageType: 'text',
    user: {
      _id: ADMIN_ID,
      name: adminName,
      avatar: adminAvatar || '',
    },
  };

  // 1. Write the message document
  const messageDocRef = await addDoc(messagesRef, messageData);

  // 2. Merge-update the thread parent document (never overwrite)
  await updateDoc(threadRef, {
    messageType: 'text',
    lastMessageText: cleanText,
    lastMessageAt: serverTimestamp(),
    lastSenderId: ADMIN_ID,
    lastSenderRole: 'admin',
    customerUnreadCount: increment(1),
    adminUnreadCount: 0,
    updatedAt: serverTimestamp(),
  });

  // 3. Optional: Trigger push notification to customer so they see the reply if app is closed
  if (customerId) {
    try {
      Axios.post(SEND_PUSH_NOTIFICATION, {
        customerId,
        id: customerId,
        title: 'Support Message',
        message: cleanText,
        type: 'support_chat',
        threadId,
      }).catch(() => {
        // Silently catch push failure so message write is unaffected
      });
    } catch (e) {
      // Ignored
    }
  }

  return messageDocRef;
};

/**
 * Uploads media file to backend S3 API and writes a message document to Firestore.
 * Strictly adheres to the Mobile/Firebase Contract:
 * - One upload endpoint: POST /api/v1/admin/uploadMedia
 * - FormData field: 'file' only (no user_id)
 * - messageType: 'image' | 'pdf' | 'doc'
 * - Single message doc in support_chats/{threadId}/messages
 * - Merge-updates thread with lastMessageText = data.url
 */
export const sendAdminMediaMessage = async ({
  threadId,
  file,
  customerId = null,
  adminName = 'Support Admin',
  adminAvatar = '',
  onProgress = null,
}) => {
  if (!firestore || !threadId || !file) {
    throw new Error('Invalid arguments for sendAdminMediaMessage');
  }

  const messageType = getMessageTypeFromFileName(file.name);
  if (!messageType) {
    throw new Error('Unsupported file format. Allowed file types: PNG, JPG, JPEG, PDF, DOC, DOCX.');
  }

  // 1. Prepare FormData for API upload
  const formData = new FormData();
  formData.append('file', file);

  const headers = {
    'Content-Type': 'multipart/form-data',
    token: localStorage.getItem('token') || '',
  };
  if (customerId) {
    headers.customerid = customerId;
  }
  const adminId = localStorage.getItem('_id');
  if (adminId) {
    headers.adminid = adminId;
  }

  // 2. Upload to API
  const response = await Axios.post(API_UPLOAD_SUPPORT_MEDIA, formData, {
    headers,
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      }
    },
  });

  const resData = response?.data;
  if (!resData || resData.status !== 'success' || !resData.data?.url) {
    const errorMsg = resData?.message || 'Media upload failed. Please try again.';
    throw new Error(errorMsg);
  }

  const fileUrl = resData.data.url;

  // 3. Construct Firestore message document strictly per contract
  const threadRef = doc(firestore, SUPPORT_CHATS_COLLECTION, threadId);
  const messagesRef = collection(firestore, SUPPORT_CHATS_COLLECTION, threadId, 'messages');

  const messageData = {
    messageType,
    fileUrl,
    fileName: file.name,
    text: messageType === 'image' ? '' : file.name,
    senderId: ADMIN_ID,
    senderRole: 'admin',
    user: {
      _id: ADMIN_ID,
      name: adminName,
      avatar: adminAvatar || '',
    },
    read: false,
    createdAt: serverTimestamp(),
  };

  // 4. Write message document
  const messageDocRef = await addDoc(messagesRef, messageData);

  // 5. Merge-update thread metadata (Critical: lastMessageText is data.url for media)
  await updateDoc(threadRef, {
    messageType,
    lastMessageText: fileUrl,
    lastMessageAt: serverTimestamp(),
    lastSenderId: ADMIN_ID,
    lastSenderRole: 'admin',
    customerUnreadCount: increment(1),
    adminUnreadCount: 0,
    updatedAt: serverTimestamp(),
  });

  // 6. Optional: Trigger push notification to customer
  if (customerId) {
    try {
      const pushTitle = 'Support Message';
      const pushBody =
        messageType === 'image'
          ? 'Sent an image'
          : messageType === 'pdf'
            ? 'Sent a PDF document'
            : 'Sent a document';

      Axios.post(SEND_PUSH_NOTIFICATION, {
        customerId,
        id: customerId,
        title: pushTitle,
        message: pushBody,
        type: 'support_chat',
        threadId,
      }).catch(() => {});
    } catch (e) {
      // Ignored
    }
  }

  return messageDocRef;
};

/**
 * Marks a conversation thread as read by the admin panel.
 * Resets adminUnreadCount to 0.
 *
 * @param {string} threadId
 */
export const markThreadAsRead = async (threadId) => {
  if (!firestore || !threadId) return;

  try {
    const threadRef = doc(firestore, SUPPORT_CHATS_COLLECTION, threadId);
    await updateDoc(threadRef, {
      adminUnreadCount: 0,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[SupportChatService] markThreadAsRead error:', error);
  }
};

/**
 * Updates the conversation status to 'open' or 'ended'.
 * Ending a thread disables sending on mobile while keeping chat history readable.
 *
 * @param {string} threadId
 * @param {'open'|'ended'} status
 */
export const updateConversationStatus = async (threadId, status) => {
  if (!firestore || !threadId) return;

  const validStatus = status === 'ended' ? 'ended' : 'open';
  const threadRef = doc(firestore, SUPPORT_CHATS_COLLECTION, threadId);

  await updateDoc(threadRef, {
    conversationStatus: validStatus,
    updatedAt: serverTimestamp(),
  });

  // CASE 2: When admin ends conversation, trigger backend closeThread to email the customer
  if (validStatus === 'ended') {
    try {
      const response = await Axios.post('/support/closeThread', {
        threadId,
        adminId: localStorage.getItem('_id'),
      });
      // eslint-disable-next-line no-console
      console.log('[SupportChatService] Backend closeThread response:', response?.data);
    } catch (apiErr) {
      // eslint-disable-next-line no-console
      console.warn(
        '[SupportChatService] Backend closeThread notification error:',
        apiErr?.message || apiErr
      );
    }
  }
};

/**
 * Subscribes to the aggregate unread count for admins across all threads.
 *
 * @param {Function} callback Callback receiving the unread count number
 * @returns {Function} Unsubscribe function
 */
export const subscribeToGlobalUnreadCount = (callback) => {
  if (!firestore) return () => {};

  try {
    const chatsRef = collection(firestore, SUPPORT_CHATS_COLLECTION);
    const q = query(chatsRef, limit(100)); // Sample recent active threads

    return onSnapshot(
      q,
      (snapshot) => {
        let totalUnread = 0;
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const count = Number(data.adminUnreadCount) || 0;
          totalUnread += count;
        });
        if (callback) callback(totalUnread);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('[SupportChatService] subscribeToGlobalUnreadCount error:', error);
      }
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[SupportChatService] subscribeToGlobalUnreadCount failed:', error);
    return () => {};
  }
};
